import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SpotlightContext = createContext(null);

export function SpotlightProvider({ children }) {
  const [spotlight, setSpotlight] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Check sessionStorage on every route change for a pending spotlight
  useEffect(() => {
    const pending = sessionStorage.getItem('pending_spotlight');
    if (pending) {
      sessionStorage.removeItem('pending_spotlight');
      try {
        const data = JSON.parse(pending);
        const timer = setTimeout(() => setSpotlight(data), 500);
        return () => clearTimeout(timer);
      } catch { /* ignore */ }
    }
  }, [location.pathname, location.search]);

  const showSpotlight = useCallback((data) => {
    setSpotlight(data);
  }, []);

  const scheduleSpotlight = useCallback((data) => {
    // Store serializable fields only (functions can't survive JSON)
    const serializable = {
      target: data.target,
      title: data.title,
      text: data.text,
      hasNext: data.hasNext,
      // Store step coords for rebuilding onNext
      nextStep: data.nextStep || null,
    };
    sessionStorage.setItem('pending_spotlight', JSON.stringify(serializable));
  }, []);

  const hideSpotlight = useCallback(() => {
    setSpotlight(null);
    sessionStorage.removeItem('pending_spotlight');
  }, []);

  // Build the advanceToNext function that navigates and shows next spotlight
  const advanceToNext = useCallback((nextStep) => {
    if (!nextStep) return;
    const { target, title, text, navigateTo, hasNext, nextStepAfter } = nextStep;
    const nextData = { target, title, text, hasNext: !!hasNext, nextStep: nextStepAfter || null };
    if (navigateTo && navigateTo !== location.pathname + location.search) {
      scheduleSpotlight(nextData);
      navigate(navigateTo);
    } else {
      setSpotlight(nextData);
    }
  }, [location, navigate, scheduleSpotlight]);

  return (
    <SpotlightContext.Provider value={{ spotlight, showSpotlight, scheduleSpotlight, hideSpotlight, advanceToNext }}>
      {children}
    </SpotlightContext.Provider>
  );
}

export function useSpotlight() {
  return useContext(SpotlightContext);
}
